/**
 * Cross-tab auth sync.
 *
 * The session is now carried by the `penn_rt` HttpOnly cookie, which every tab
 * shares (ADR-0036), so tabs no longer need help STAYING signed in — but they
 * do still need to be told when a session ENDS, because each tab holds its own
 * in-memory access token that no cookie change can clear.
 *
 * Only a DELIBERATE logout broadcasts. An unrecoverable 401 deliberately does
 * not (see `AxiosService`): one stale background tab used to evict every other
 * tab, valid sessions included, which was the direct cause of the reported
 * "logged out several times a day".
 *
 * Transport: `BroadcastChannel` where available, with a `localStorage`
 * `storage`-event fallback for older browsers. The fallback only writes a
 * transient signal value (immediately removed) — the session itself is never
 * placed in localStorage. The `storage` event never fires in the tab that
 * triggered it, and a BroadcastChannel does not echo to its own sender, so the
 * initiating tab does not re-handle its own logout.
 */
const CHANNEL_NAME = "penn-auth-sync";
const STORAGE_PING_KEY = "penn-auth-sync-ping";

type AuthSyncMessage = { type: "logout" };

const channel: BroadcastChannel | null =
  typeof window !== "undefined" && "BroadcastChannel" in window
    ? new BroadcastChannel(CHANNEL_NAME)
    : null;

/** Tell every other open tab to log out. */
export function broadcastLogout(): void {
  try {
    channel?.postMessage({ type: "logout" } satisfies AuthSyncMessage);
  } catch {
    /* ignore — fallback below */
  }
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(STORAGE_PING_KEY, `logout:${Date.now()}`);
      window.localStorage.removeItem(STORAGE_PING_KEY);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Run `onLogout` whenever ANOTHER tab broadcasts a logout. Returns an
 * unsubscribe function.
 */
export function subscribeAuthSync(onLogout: () => void): () => void {
  if (typeof window === "undefined") return () => {};

  const onMessage = (e: MessageEvent) => {
    if ((e.data as AuthSyncMessage | undefined)?.type === "logout") onLogout();
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_PING_KEY && e.newValue?.startsWith("logout:")) {
      onLogout();
    }
  };

  channel?.addEventListener("message", onMessage);
  window.addEventListener("storage", onStorage);

  return () => {
    channel?.removeEventListener("message", onMessage);
    window.removeEventListener("storage", onStorage);
  };
}
